import { useScheduleWorkflowsQuery } from '@/hooks/api/use-schedule-workflows-query'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import { SquareArrowOutUpRightIcon } from 'lucide-react'
import { TabsPanelId, useSplitView } from '@/app/_common/split-view/split-view-context'
import { createWorkflowTab } from '@/app/_common/split-view/tabs/factories'
import React from 'react'
import { TAB_TYPE_CHAT, TabTypeStatic } from '@/app/_common/split-view/tabs/types'
import { T, useTranslate } from '@tolgee/react'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn, formatDate } from '@/lib/utils'
import { WORKFLOW_STATUS } from '@/app/chat-history/chat-history'

type ScheduleWorkflowProps = {
  scheduleId: string
}

export function ScheduleWorkflows({ scheduleId }: ScheduleWorkflowProps) {
  const { data, isLoading, isError } = useScheduleWorkflowsQuery({ scheduleId })
  const { addTab, getTabPositionMetadata } = useSplitView()
  const { t } = useTranslate()
  const getOppositePanelId = React.useCallback(() => {
    const position = getTabPositionMetadata({ tabType: TabTypeStatic.Schedule })
    return position?.panelId === TabsPanelId.Right ? TabsPanelId.Left : TabsPanelId.Right
  }, [getTabPositionMetadata])

  const getChatIndex = React.useCallback(
    ({ workflowId }: { workflowId: string }) => {
      const position = getTabPositionMetadata({ tabType: TAB_TYPE_CHAT, tabId: workflowId })
      return position?.index ?? 0
    },
    [getTabPositionMetadata],
  )

  const onOpenWorkflow = ({ workflowId, title }: { workflowId: string; title: string }) => {
    addTab({
      tab: createWorkflowTab({
        workflowId,
        title,
      }),
      index: getChatIndex({ workflowId }),
      panelId: getOppositePanelId(),
    })
  }

  return (
    <div className='max-h-[164px] overflow-auto'>
      {isError && (
        <p className='py-4 text-center text-sm font-light text-error-300'>
          <T keyName='shared.error.system' />
        </p>
      )}
      {isLoading && (
        <div className='flex-col space-y-2 pb-4 pr-6'>
          <Skeleton className='h-10' />
          <Skeleton className='h-10' />
          <Skeleton className='h-10' />
        </div>
      )}
      {data?.items && !data?.items?.length && (
        <p className='py-4 text-center text-sm font-light text-neutral-500'>
          <T keyName='schedule.table.workflows.noResults' />
        </p>
      )}
      {data?.items?.map((item) => {
        const statusColor = WORKFLOW_STATUS[item?.status]?.color
        return (
          <div
            key={item?.workflow_id}
            className='flex items-center gap-4 border-b border-neutral-75 py-3.5 pl-2 last:border-b-0'
          >
            <Tooltip delayDuration={0}>
              <TooltipTrigger>
                <div
                  className={cn(`h-2 w-2 rounded-full`, {
                    'bg-black/60': statusColor === 'neutral',
                    'bg-success-300': statusColor === 'success',
                    'bg-error-300': statusColor === 'error',
                    'bg-mango-300': statusColor === 'mango',
                  })}
                />
              </TooltipTrigger>
              <TooltipContent size='sm' sideOffset={8} side='bottom' align='center'>
                {WORKFLOW_STATUS[item?.status]?.name}
              </TooltipContent>
            </Tooltip>
            <p className='text-xs font-normal'>
              {formatDate(item?.last_update_time, { template: 'DD MMMM YYYY, hh:mm:ss A' })}
            </p>
            <TooltipIconButton
              titleString={t('schedule.table.workflows.openWorkflow')}
              size={'xs'}
              onClick={() => onOpenWorkflow({ workflowId: item?.workflow_id, title: item?.title })}
              contentProps={{ side: 'bottom' }}
            >
              <SquareArrowOutUpRightIcon className='text-neutral-150' />
            </TooltipIconButton>
          </div>
        )
      })}
    </div>
  )
}
